import ActionDelete from "@mui/icons-material/Delete"
import { alpha } from "@mui/material/styles"
import { ConfirmDialog } from "components/ConfirmDialog"
import type { FC } from "react"
import React, { useCallback, useState } from "react"
import {
  Button,
  useDeleteMany,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from "react-admin"
import { useDivisionId, useStyles } from "utils"

const groupNs = "adminPanel.groups"

export const ListDeleteButton: FC = () => {
  const notify = useNotify()
  const refresh = useRefresh()
  const { selectedIds } = useListContext()
  const [divisionId] = useDivisionId()
  const [deleteMany, { isLoading }] = useDeleteMany()

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const onCloseConfirmDialog = useCallback(() => setOpenConfirmDialog(false), [])
  const handleClick = useCallback(() => setOpenConfirmDialog(true), [])

  const unselectAll = useUnselectAll(`universe/division/${divisionId}/groups`)

  const deleteGroups = async (): Promise<void> =>
    deleteMany(
      `universe/division/${divisionId}/groups`,
      {
        ids: selectedIds,
      },
      {
        onSuccess: () => {
          notify("ra.notification.deleted", {
            type: "success",
            messageArgs: { smart_count: selectedIds.length },
          })
          unselectAll()
          onCloseConfirmDialog()
          // Refresh is not automatic on non-related lists
          refresh()
        },
        onError: (e: any) => {
          notify(
            e.response?.data?.code
              ? "adminPanel.groups.groupDeleteError"
              : "ra.notification.http_error",
            {
              type: "error",
            },
          )
          refresh()
        },
        mutationMode: "optimistic",
      },
    )

  const s = useStyles(({ palette }) => ({
    button: {
      color: palette.error.main,
      "&:hover": {
        backgroundColor: alpha(palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }))

  return (
    <>
      <ConfirmDialog
        onClose={onCloseConfirmDialog}
        onConfirm={deleteGroups}
        open={openConfirmDialog}
        namespace={groupNs}
      />
      <Button sx={s.button} disabled={isLoading} onClick={handleClick} label="ra.action.delete">
        <ActionDelete />
      </Button>
    </>
  )
}
